import { DesignPhoto } from '@/components/DesignPhoto';
import { Button } from '@/components/PageHeading';
import { useEscapeKey } from '@/components/shortcuts';
import type { GarmentSheet as Sheet } from '@core/bookings/garmentSheet';
import { formatAmount } from '@core/format/money';
import { withReference } from '@core/units/referenceNumber';

import { MoneyLine, MoneyLines } from './MoneyLine';

/**
 * One garment in the booking being taken, opened from the bag or the party — the booking
 * redesign's per-garment sheet. What it comes to, its own discount, what goes out with it and who
 * wears it, then every way to change it: another size or colour of the same design, another design,
 * another person, or out of the booking.
 *
 * <p>
 * <strong>It edits the draft, not a record</strong>, so the insert/update rule does not reach it —
 * the booking is written once, by Confirm. The discount and accessory editors stay their own
 * popups, opened from here: both are already shared with the amendment screen, and a second copy
 * inside this one is a second place for the rules to drift. Every word on it is the core's
 * `garmentSheet`, which the phone's sheet reads too.
 * </p>
 */
export function GarmentSheet({
  sheet,
  currency,
  busy,
  onClose,
  onDiscount,
  onAccessories,
  onChangeSize,
  onSwapDesign,
  onMove,
  onRemove,
}: {
  sheet: Sheet;
  currency: string;
  busy: boolean;
  onClose: () => void;
  onDiscount: () => void;
  onAccessories: () => void;
  /** Opens this design's page to swap for another size or colour of it. */
  onChangeSize: () => void;
  /** Sends the rail into swapping this garment for whatever is picked next. */
  onSwapDesign: () => void;
  onMove: (wearerKey: string) => void;
  onRemove: () => void;
}) {
  useEscapeKey(onClose);

  const cash = (amount: number) => `${currency}${formatAmount(amount)}`;

  return (
    <div
      data-shortcut-layer
      className="fixed inset-0 z-9999 flex items-center justify-center p-4 print:hidden"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
      />

      <div
        role="dialog"
        aria-label={`${sheet.name}, ${sheet.serialCode}`}
        className="border-[var(--overlay-border)] relative flex max-h-full w-full max-w-[440px] flex-col overflow-y-auto rounded border bg-[var(--overlay-surface)] shadow-lg"
      >
        <header className="border-border-color flex items-center gap-3 border-b px-4 py-3">
          <div className="w-12 shrink-0 overflow-hidden rounded">
            <DesignPhoto url={sheet.imageUrl} alt={sheet.name} categoryPath={sheet.categoryName} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="mb-0 truncate text-[length:var(--scale-list-name)] font-semibold text-gray-900">
              {sheet.name}
            </p>
            <p className="text-default font-mono-nums mb-0 truncate text-[length:var(--scale-list-meta)]">
              {withReference(sheet.serialCode, sheet.referenceNumber)}
            </p>
            <p className="text-default mb-0 truncate text-[length:var(--scale-list-meta)]">
              For <b className="text-gray-900">{sheet.wearerName}</b>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-default flex size-8 shrink-0 cursor-pointer items-center justify-center rounded hover:bg-light hover:text-gray-900"
          >
            <i className="icon icon-x" />
          </button>
        </header>

        <div className="flex flex-col gap-4 px-4 py-3">
          <MoneyLines>
            {sheet.rate === null ? (
              <MoneyLine label="Rate" value="Nothing prices it" tone="warning" />
            ) : (
              <>
                <MoneyLine label="Rate for these days" value={cash(sheet.rate)} />
                {sheet.off > 0 && (
                  <MoneyLine label="Its own discount" value={`− ${cash(sheet.off)}`} tone="brand" />
                )}
                <MoneyLine label="Comes to" value={cash(sheet.charged ?? 0)} strong />
              </>
            )}
          </MoneyLines>

          <Row
            label="Discount on this garment"
            value={sheet.discountWords}
            action={
              sheet.canDiscount ? (
                <Button disabled={busy} onClick={onDiscount}>
                  {sheet.off > 0 ? 'Change' : 'Give one'}
                </Button>
              ) : null
            }
          />

          <Row
            label="Goes out with it"
            value={sheet.accessoryWords}
            action={
              <Button disabled={busy} onClick={onAccessories}>
                Edit
              </Button>
            }
          />

          <div>
            <p className="text-default mb-1.5 text-[length:var(--scale-list-meta)] font-semibold uppercase">
              Change it
            </p>
            <div className="flex flex-wrap gap-2">
              <Button disabled={busy} icon="icon icon-ruler" onClick={onChangeSize}>
                Another size or colour
              </Button>
              <Button disabled={busy} icon="icon icon-repeat" onClick={onSwapDesign}>
                Another design
              </Button>
            </div>
          </div>

          {sheet.movesTo.length > 0 && (
            <div>
              <p className="text-default mb-1.5 text-[length:var(--scale-list-meta)] font-semibold uppercase">
                Give it to
              </p>
              <div className="flex flex-wrap gap-2">
                {sheet.movesTo.map((wearer) => (
                  <Button key={wearer.key} disabled={busy} onClick={() => onMove(wearer.key)}>
                    {wearer.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        <footer className="border-border-color flex items-center gap-2 border-t px-4 py-3">
          <Button variant="danger" disabled={busy} onClick={onRemove}>
            Take it out
          </Button>
          <span className="me-auto" />
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </footer>
      </div>
    </div>
  );
}

function Row({ label, value, action }: { label: string; value: string; action: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-default mb-0 text-[length:var(--scale-list-meta)] font-semibold uppercase">
          {label}
        </p>
        <p className="mb-0 text-[length:var(--scale-list-name)] text-gray-900">{value}</p>
      </div>
      {action}
    </div>
  );
}
